import React, { useEffect, useState } from 'react';
import { CountryCode } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Carousel from '@components/shared/Carousel';
import Colors from '@components/shared/Colors';
import TextLinkButton from '@components/shared/TextLinkButton';
import SimpleProductBox from '@components/storefront/SimpleProductBox';
import { ProductCodeKey } from '@contracts';
import { getSimpleShopProducts } from '@data/simpleShopProducts';
import CarouselChevron from '@images/carousel-chevron.svg';
import useCarousel from '@utils/useCarousel';
import useNavigate from '@utils/useNavigate';

interface Props {
  country?: CountryCode;
  textColor?: string;
  productCode?: ProductCodeKey;
  customProducts?: { product_id: ProductCodeKey }[];
}

const flickityOptions = {
  initialIndex: 2,
  groupCells: 1,
  pageDots: false,
  prevNextButtons: false,
  contain: true,
  percentPosition: false,
  draggable: true,
  cellAlign: 'center'
};

const SimpleProductCarousel: React.FC<Props> = ({ productCode, country, customProducts, textColor = Colors.navyBlue }) => {
  const { navigateWrapper } = useNavigate();
  const [show, setShow] = useState(false);
  const { t } = useTranslation();
  const shopProducts = getSimpleShopProducts(country, productCode, customProducts);
  const { carouselRef, carouselNext, carouselPrevious, previousDisabled, nextDisabled } = useCarousel({ initialIndex: 2 });

  useEffect(() => {
    setShow(true);
  }, []);

  if (!show) {
    return null;
  }

  return (
    <Container>
      <Header data-testid="carousel-header">
        <Title>{t('components.more-from-daye')}</Title>
        <Controls>
          <ArrowsWrap>
            <CarouselPrevious disabled={previousDisabled} onClick={carouselPrevious} color={textColor} aria-label="previous products">
              <CarouselChevron />
            </CarouselPrevious>
            <CarouselNext disabled={nextDisabled} onClick={carouselNext} color={textColor} aria-label="next products">
              <CarouselChevron />
            </CarouselNext>
          </ArrowsWrap>
          <LinkWrap>
            <StyledTextLinkButton onClick={() => navigateWrapper('/products/')} $color={textColor}>
              {t('components.all-products')}
            </StyledTextLinkButton>
          </LinkWrap>
        </Controls>
      </Header>
      <CarouselContainer data-testid="carousel-container">
        <Carousel options={flickityOptions} ref={carouselRef}>
          {shopProducts.map((product) => (
            <ProductBoxWrap key={product.product_code}>
              <SimpleProductBox data={product} textColor={textColor} />
            </ProductBoxWrap>
          ))}
        </Carousel>
      </CarouselContainer>
    </Container>
  );
};

export default SimpleProductCarousel;

interface StyleProps {
  textColor?: string;
}

const Container = styled.div<StyleProps>`
  position: relative;
  margin: 120px auto 120px auto;
  width: 100%;
  height: 380px;
  overflow: hidden;
  color: ${(props: StyleProps) => props.textColor || Colors.navyBlue};

  @media (max-width: 768px) {
    margin: 40px auto 40px auto;
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto 35px;

  @media (max-width: 1200px) {
    padding: 0 20px 0 25px;
  }

  @media (max-width: 768px) {
    margin: 0 auto 15px;
  }
`;

const Title = styled.p`
  text-transform: uppercase;
  font-family: BrownStd;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 21px;
  letter-spacing: 1.5px;
  text-align: left;
  color: inherit;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-direction: row;
`;

const ArrowsWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const CarouselNext = styled.button<StyleProps>`
  background: none;
  border: 0;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0 5px;

  & path {
    fill: ${(props: StyleProps) => props.textColor};
  }

  &:disabled {
    cursor: default;
    opacity: 0.30000001192092896;
  }

  @media (max-width: 768px) {
    svg {
      height: 15px;
    }
  }

  :hover {
    fill: ${Colors.darkGreen};
  }
`;

const CarouselPrevious = styled(CarouselNext)`
  transform: rotate(180deg);
`;

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  .flickity-viewport {
    overflow: visible;
    transform: translateZ(0);
  }

  .carousel:after {
    content: 'flickity';
    display: none; /* hide :after */
  }

  @media (max-width: 1200px) {
    padding: 0 20px;
  }
`;

const ProductBoxWrap = styled.div`
  margin: 0 20px 0 20px;

  @media (max-width: 1200px) {
    margin: 0 10px 0 10px;
  }
`;

const LinkWrap = styled.div`
  display: inline-block;

  @media (max-width: 768px) {
    display: block;
    position: absolute;
    bottom: 0;
    width: 100%;
    right: 0;
    text-align: center;
  }
`;

const StyledTextLinkButton = styled(TextLinkButton)`
  font-size: 15px;
  font-weight: 700;
  line-height: 160%;
  margin-left: 40px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;
